import { Component, HostBinding, Inject, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IDType } from '@core/models/types';
import { isArrayLike } from '@utils/array';
import { isString } from '@utils/type';
import { PromptDialogData, PromptDialogField } from './models';

export type MirPromptType =
  'standard'
  | 'primary'
  | 'accent'
  | 'warn'
  | 'danger'
  | 'slate'
  | 'blue'
  ;

const MirPromptHeaderBaseCss = `block gap-y-8 py-4 px-4 text-lg font-bold truncate`;

const MirPromptHeaderTiTle: { [key in MirPromptType]: string } = {
  'standard': `${MirPromptHeaderBaseCss} bg-white text-gray-900`,
  'primary': `white-header flex items-center gap-x-2 py-4 px-4 text-lg font-bold truncate bg-white text-gray-900`,
  'accent': `${MirPromptHeaderBaseCss} bg-green-100 text-gray-900`,
  'warn': `${MirPromptHeaderBaseCss} bg-red-900 text-white`,
  'danger': `${MirPromptHeaderBaseCss} bg-orange-700 text-white`,
  'slate': `${MirPromptHeaderBaseCss} bg-slate-50 text-gray-900`,
  'blue': `${MirPromptHeaderBaseCss} flex items-center gap-x-2 bg-[var(--mir-color-blue-dark-800)] text-white`,
};

const DEFAULT_MAX_LENGTH = Number.MAX_SAFE_INTEGER;

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'mir-prompt-dialog, prompt-dialog',
  templateUrl: 'prompt-dialog.component.html',
  styleUrls: ['prompt-dialog.component.scss'],
  standalone: false
})
export class PromptDialogComponent implements OnInit {

  @HostBinding('class')
  get hostCssClass(): string {
    return `mir-prompt-dialog ${this.type}`;
  }

  public readonly dialogRef = inject(MatDialogRef<PromptDialogComponent>);
  private readonly fb = inject(FormBuilder);

  title: string;
  messages: { id: IDType; value: string; }[];
  icon: string;
  type: MirPromptType = 'standard';
  promptForm: FormGroup;
  inputIds: { [key: string]: string; } = {};
  canDisplaySubTitle: boolean;
  required: boolean;
  maxLength: number;
  hintLabel: string;
  confirmLabel: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PromptDialogData,
  ) {
    this.title = data.title;
    this.type = data.type || 'standard';
    this.icon = data.icon;
    this.maxLength = Math.max(0, (data.maxLength ?? DEFAULT_MAX_LENGTH));
    this.hintLabel = data.hintLabel;
    this.confirmLabel = data.confirmLabel;

    if (data.messages) {
      const messages = data.messages;

      if (isString(messages)) {
        this.messages = [{ id: 0, value: messages as string }];
      }
      else if (isArrayLike(messages)) {
        this.messages = (messages as string[]).map((message, index) => ({ id: index, value: message }));
      }
      else {
        this.messages = [];
      }
    }
    else {
      this.messages = [];
    }
    this.required = !!data.required;
  }

  get headerCssClass(): string {
    return MirPromptHeaderTiTle[this.type];
  }

  ngOnInit() {
    const formGroup = {};

    this.data.fields.forEach((field) => {
      const validators = [];

      if (field.mandatory) {
        validators.push(Validators.required);
      }

      if (field.type === 'email') {
        validators.push(Validators.email);
        validators.push(Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/));
      }

      formGroup[field.name] = [field.value, validators];
      this.inputIds[field.name] = `${field.name}-${Date.now()}`;
    });
    this.promptForm = this.fb.group(formGroup);
  }

  handleNoClick() {
    this.dialogRef.close();
  }

  handleBlur(field: PromptDialogField) {
    this.data.fields[this.data.fields.indexOf(field)].value = this.promptForm.controls[field.name].value;
  }

  handleInput(field: PromptDialogField) {
    this.data.fields[this.data.fields.indexOf(field)].value = this.promptForm.controls[field.name].value;
  }

  handleSubmit() {
    if (this.promptForm.valid) {
      this.dialogRef.close(this.data);
    }
    else {
      this.promptForm.markAllAsTouched();
    }
  }

  getInputId(fieldName: string): string {
    return this.inputIds[fieldName];
  }
}
